import { useState, useMemo } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  getTransitionsPossibles,
  STATUTS_CONFIG,
  canBeArchived,
  StatutChantier,
} from "@/constants/statuts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { Building, Calendar, MoreVertical, Edit, Trash2, Archive, RefreshCw, Clock, Timer, Play, Pause, X, CheckCircle, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { cn } from "@/lib/utils";
import { ChantierIcon } from "@/components/chantiers/ChantierIcon";
import { SuspensionBadge } from "@/components/project/SuspensionBadge";
import { getWorkEndHourForDate } from "@/lib/disponibilite-utils";
import { addBusinessDays } from "@/lib/heures-utils";
import { JourDetail } from "@/types/calendar";

interface ProjectCardProps {
  id: string;
  nom_chantier: string;
  client: string;
  rentabilite: number;
  jours_restants?: number;
  budget_devis?: number;
  couts_engages?: number;
  etat_chantier?: string;
  statut?: string;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
  duree_estimee_jours?: number; // Durée en jours ouvrés (supporte les décimales: 3.5)
  jours_details?: JourDetail[] | null; // Détails des jours/demi-journées travaillés
  heures_par_jour?: number; // Heures par jour (depuis première affectation, défaut 8h)
  pictogramme?: string;
  couleur?: string;
  hasAiAnalysis?: boolean;
  hasDelayRegistered?: boolean; // True si un retard est enregistré sur ce chantier
  dateDebutRetard?: string; // Date de début du retard (si enregistré)
  dateFinRetard?: string;   // Date de fin du retard (si enregistré)
  joursRetard?: number; // Nombre de jours ouvrés de retard (calculé correctement avec demi-journées)
  date_fin_initiale?: string; // Date de fin AVANT ajout de retard (pour afficher période initiale)
  isHighlighted?: boolean; // Pour l'effet de highlight depuis la checklist
  // Props pour la gestion des états de la barre de progression
  suspensionDate?: string; // Date de suspension (si suspendu)
  suspensionRepriseEstimee?: string; // Date de reprise estimée (si définie)
  dateReprise?: string; // Date de reprise effective (pour afficher badge "Repris" 48h)
  progress_pct?: number; // Progression temporelle calculée côté serveur (0-100)
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onChangeState?: (id: string, newState: string) => void;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onCloturer?: (id: string) => void; // Callback pour ouvrir la modal de clôture
  onDemarrerBrouillon?: (id: string) => void; // Callback pour démarrer un brouillon (vérifie dates et conflits)
  onProlongerRetard?: (id: string) => void; // Callback pour prolonger le retard (navigue vers Penalty Simulator)
}

const ProjectCard = ({
  id,
  nom_chantier,
  client,
  rentabilite,
  jours_restants,
  budget_devis = 0,
  couts_engages = 0,
  etat_chantier = 'en_cours',
  statut = 'actif',
  date_debut_prevue,
  date_fin_estimee,
  duree_estimee_jours,
  jours_details,
  heures_par_jour,
  pictogramme,
  couleur,
  hasAiAnalysis = false,
  hasDelayRegistered = false,
  dateDebutRetard,
  dateFinRetard,
  joursRetard,
  date_fin_initiale,
  isHighlighted = false,
  suspensionDate,
  suspensionRepriseEstimee,
  dateReprise,
  progress_pct,
  onEdit,
  onDelete,
  onChangeState,
  onArchive,
  onUnarchive,
  onCloturer,
  onDemarrerBrouillon,
  onProlongerRetard,
}: ProjectCardProps) => {
  const navigate = useNavigate();

  // === STATES POUR LES BADGES AVEC FLÈCHES ===
  // Date dépassée : Clôturer ↔ Retard
  const [badgeMode, setBadgeMode] = useState<'cloturer' | 'retard'>('cloturer');
  // Terminé : Terminé ↔ Archiver
  const [termineBadgeMode, setTermineBadgeMode] = useState<'reouvrir' | 'archiver'>('reouvrir');
  // Annulé : Archiver ↔ Supprimer
  const [annuleBadgeMode, setAnnuleBadgeMode] = useState<'archiver' | 'supprimer'>('archiver');
  // Suspendu : Suspendu ↔ Reprise ↔ Annuler ↔ Terminer
  const [suspenduBadgeMode, setSuspenduBadgeMode] = useState<'suspendu' | 'reprise' | 'annuler' | 'terminer'>('suspendu');
  // Archivé : Désarchiver ↔ Supprimer
  const [archiveBadgeMode, setArchiveBadgeMode] = useState<'desarchiver' | 'supprimer'>('desarchiver');
  // Retard enregistré : Modifier ↔ Clôturer
  const [retardBadgeMode, setRetardBadgeMode] = useState<'modifier' | 'cloturer'>('modifier');
  // Fin retard dépassée : Clôturer ↔ Prolonger
  const [finRetardBadgeMode, setFinRetardBadgeMode] = useState<'cloturer' | 'prolonger'>('cloturer');
  // Brouillon : Démarrer ↔ Supprimer
  const [brouillonBadgeMode, setBrouillonBadgeMode] = useState<'demarrer' | 'supprimer'>('demarrer');
  // Repris récemment : Repris ↔ Suspendre
  const [reprisBadgeMode, setReprisBadgeMode] = useState<'repris' | 'suspendre'>('repris');
  // Modal de confirmation suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);

  const etatConfig: Record<string, { label: string; color: string; icon: string }> = {
    'brouillon': { label: 'Brouillon', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300', icon: '📝' },
    'devis_envoye': { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', icon: '📊' },
    'planifie': { label: 'Planifié', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', icon: '📅' },
    'en_cours': { label: 'En cours', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', icon: '🏗️' },
    'suspendu': { label: 'Suspendu', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', icon: '⏸️' },
    'termine': { label: 'Terminé', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300', icon: '✅' },
    'annule': { label: 'Annulé', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', icon: '❌' },
    'archive': { label: 'Archivé', color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300', icon: '📦' },
  };
  
  const currentEtat = etatConfig[etat_chantier] || etatConfig['en_cours'];
  const rentabilityBadge = getRentabilityBadge(rentabilite);
  const progressValue = budget_devis > 0 ? (couts_engages / budget_devis) * 100 : 0;

  // Calculer les transitions disponibles depuis le statut actuel
  // Utilise les règles centralisées de statuts.ts
  const availableTransitions = useMemo(() => {
    return getTransitionsPossibles(
      etat_chantier as StatutChantier,
      date_debut_prevue,
      null, // date_debut_reelle (pas disponible dans les props)
      date_fin_estimee,
      null  // date_fin_reelle (pas disponible dans les props)
    );
  }, [etat_chantier, date_debut_prevue, date_fin_estimee]);

  // Vérifie si le chantier peut être archivé
  const canArchiveChantier = useMemo(() => {
    return canBeArchived(etat_chantier as StatutChantier);
  }, [etat_chantier]);

  // Formatage des dates
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatDateFull = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Vérifier si la date de fin est dépassée (basé sur le dernier jour d'affectation)
  // Priorité: jours_details (day selector) > date_fin_estimee (fallback)
  // AM → dépassé après 13h, FULL/PM → dépassé après 17h
  const isDateFinDepassee = useMemo(() => {
    const now = new Date();
    const currentHour = now.getHours() + now.getMinutes() / 60;
    const todayStr = now.toISOString().split('T')[0];

    // Priorité 1 : Dernier jour dans jours_details (day selector des affectations)
    if (jours_details && jours_details.length > 0) {
      const sorted = [...jours_details].sort((a, b) => b.date.localeCompare(a.date));
      const lastDay = sorted[0];
      const lastDayDate = lastDay.date;

      if (todayStr > lastDayDate) return true;
      if (todayStr === lastDayDate) {
        const endHour = lastDay.period === 'AM' ? 13 : 17;
        return currentHour >= endHour;
      }
      return false;
    }

    // Fallback : date_fin_estimee avec fin à 17h par défaut
    if (!date_fin_estimee) return false;
    const dateFinStr = new Date(date_fin_estimee).toISOString().split('T')[0];

    if (todayStr > dateFinStr) return true;
    if (todayStr === dateFinStr) return currentHour >= 17;
    return false;
  }, [jours_details, date_fin_estimee]);

  // Nombre de jours de retard déclarés (pour badge et frise)
  // Utilise la prop joursRetard (jours ouvrés depuis le serveur)
  const joursRetardDeclareGlobal = hasDelayRegistered ? (joursRetard ?? 0) : 0;

  // Afficher le badge retard dès qu'un retard est déclaré (peu importe si date dépassée)
  const showDelayBadge = joursRetardDeclareGlobal > 0;

  // Vérifier si la fin de la période de retard est dépassée
  // Utilise dateFinRetard + jours_details pour la période du dernier jour
  const isFinRetardDepassee = useMemo(() => {
    if (!hasDelayRegistered || !dateFinRetard || etat_chantier !== 'en_cours') return false;
    const now = new Date();
    const currentHour = now.getHours() + now.getMinutes() / 60;
    const todayStr = now.toISOString().split('T')[0];
    const dateFinStr = new Date(dateFinRetard).toISOString().split('T')[0];

    if (todayStr > dateFinStr) return true;
    if (todayStr === dateFinStr) {
      const workEndHour = getWorkEndHourForDate(jours_details, dateFinStr);
      return currentHour >= workEndHour;
    }
    return false;
  }, [hasDelayRegistered, dateFinRetard, etat_chantier, jours_details]);

  // Style atténué si suspendu
  const isSuspendu = etat_chantier === 'suspendu';

  return (
    <Card className={cn(
      "card-premium hover-lift group animate-scale-in flex flex-col",
      isSuspendu && "opacity-90 border-amber-500/30 bg-amber-500/5"
    )}>
      <CardHeader className="p-4 pb-2">
        {/* Ligne 1: Badges d'état + IA + Rentabilité + Menu */}
        <div className="flex items-center justify-between gap-1.5 mb-2">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className={cn("font-medium text-[10px] px-1.5 py-0.5 leading-tight cursor-help", currentEtat.color)}>
                  {currentEtat.icon} {currentEtat.label}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4} avoidCollisions={false} className="text-xs">
                État actuel du chantier
              </TooltipContent>
            </Tooltip>
            {/* Badge IA - Cliquable vers l'onglet Analyse IA */}
            {hasAiAnalysis && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    className="badge-clickable bg-purple-500/10 text-purple-600 dark:text-purple-400 font-medium text-[10px] px-1.5 py-0.5 leading-tight"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${id}?tab=ai-analysis`);
                    }}
                  >
                    🤖 IA
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4} avoidCollisions={false} className="text-xs">
                  Cliquer pour voir l'analyse IA
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Badge Rentabilité - Cliquable vers l'onglet Rentabilité */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className={cn(
                    "badge-clickable font-semibold text-[10px] px-1.5 py-0.5 border whitespace-nowrap leading-tight",
                    rentabilityBadge.bgColor,
                    rentabilityBadge.color
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/financial?tab=profitability', { state: { chantierId: id } });
                  }}
                >
                  {rentabilityBadge.emoji} {rentabilityBadge.label}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4} avoidCollisions={false} className="text-xs">
                Cliquer pour voir la rentabilité détaillée
              </TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit?.(id)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </DropdownMenuItem>

                {/* Menu différent si suspendu */}
                {etat_chantier === 'suspendu' ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Statut
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => onChangeState?.(id, 'reprise')}
                        className="text-green-600"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Reprendre
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onChangeState?.(id, 'termine')}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Terminer
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onChangeState?.(id, 'annule')}
                        className="text-destructive"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Annuler
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Statut
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {/* Affiche UNIQUEMENT les transitions valides depuis le statut actuel */}
                      {availableTransitions.length > 0 ? (
                        availableTransitions.map((transition) => {
                          const config = STATUTS_CONFIG[transition.statut];
                          return (
                            <DropdownMenuItem
                              key={transition.statut}
                              onClick={() => !transition.disabled && onChangeState?.(id, transition.statut)}
                              className={cn(
                                transition.disabled && "opacity-50 cursor-not-allowed"
                              )}
                              disabled={transition.disabled}
                              title={transition.disabledReason || transition.description}
                            >
                              {config?.icon || ''} {transition.label}
                              {transition.modal && <span className="ml-1 text-xs opacity-60">…</span>}
                            </DropdownMenuItem>
                          );
                        })
                      ) : (
                        <DropdownMenuItem disabled className="text-muted-foreground">
                          Aucune transition disponible
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}

                {/* Archiver - UNIQUEMENT si le statut permet l'archivage */}
                {canArchiveChantier && (
                  <DropdownMenuItem onClick={() => onArchive?.(id)}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archiver
                  </DropdownMenuItem>
                )}

                {/* Désarchiver - UNIQUEMENT si archivé */}
                {etat_chantier === 'archive' && (
                  <DropdownMenuItem onClick={() => onUnarchive?.(id)}>
                    <Archive className="h-4 w-4 mr-2" />
                    Désarchiver
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete?.(id)} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Ligne 2: Nom du chantier avec pictogramme + Badge retard */}
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-bold flex items-center gap-2 group-hover:text-amber-400 transition-colors leading-tight min-w-0">
            {/* Pictogramme personnalisé ou icône par défaut */}
            {pictogramme && couleur ? (
              <ChantierIcon
                pictogramme={pictogramme}
                couleur={couleur}
                size="md"
                showBackground={true}
              />
            ) : (
              <div className="p-1 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors shrink-0">
                <Building className="h-3.5 w-3.5 text-primary" />
              </div>
            )}
            <span className="truncate">{nom_chantier}</span>
          </CardTitle>
          {/* Badge Retard - Cliquable vers l'onglet Retard */}
          {showDelayBadge && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className="badge-clickable bg-orange-500/15 text-orange-500 border border-orange-500/30 font-semibold text-[10px] px-1.5 py-0.5 leading-tight shrink-0 flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/financial?tab=retard', { state: { chantierId: id } });
                  }}
                >
                  <Timer className="h-3 w-3" />
                  +{Math.round(joursRetardDeclareGlobal)}j retard
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4} className="text-xs">
                Cliquer pour gérer les pénalités de retard
              </TooltipContent>
            </Tooltip>
          )}
          {/* Badge Suspension - Cliquable pour reprendre */}
          {etat_chantier === 'suspendu' && (
            <SuspensionBadge
              chantierId={id}
              etatChantier={etat_chantier}
              onClick={() => onChangeState?.(id, 'reprise')}
              compact
            />
          )}
        </div>

        {/* Ligne 3: Client */}
        <p className="text-xs text-muted-foreground mt-1 font-medium truncate">{client}</p>
      </CardHeader>
      
      <CardContent className="p-4 pt-2 space-y-3 flex-1">
        {/* Budget utilisé - Style badge avec montant */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-between cursor-help">
              <span className="text-muted-foreground text-xs font-medium flex items-center gap-1.5">
                💰 Budget dépensé
              </span>
              <div className="px-2.5 py-1 rounded-md bg-slate-800/80 border border-slate-700/50">
                <span className="text-xs font-bold text-white">
                  {progressValue.toFixed(0)}%
                  <span className="text-muted-foreground mx-1.5">•</span>
                  {couts_engages.toLocaleString('fr-FR')}€
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {couts_engages.toLocaleString('fr-FR')}€ sur {budget_devis.toLocaleString('fr-FR')}€
          </TooltipContent>
        </Tooltip>

        {/* KPIs en grille */}
        <div className="grid grid-cols-2 gap-2">
          {/* Rentabilité */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="p-2 rounded-md bg-muted/30 space-y-0.5 cursor-help">
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider">
                  Bénéfice estimé
                </p>
                <p className={cn(
                  "text-xl font-black font-mono leading-none",
                  // Seuils harmonisés BTP: 18% = BON (vert), 12% = CORRECT (jaune), 0-12% = orange, <0% = rouge
                  rentabilite >= 18 ? "text-green-500" :
                  rentabilite >= 12 ? "text-yellow-500" :
                  rentabilite >= 0 ? "text-orange-500" : "text-red-500"
                )}>
                  {rentabilite.toFixed(1)}%
                </p>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Marge nette prévue en fin de chantier
            </TooltipContent>
          </Tooltip>

          {/* Marge rentable */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="p-2 rounded-md bg-muted/30 space-y-0.5 cursor-help">
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider flex items-center gap-0.5">
                  <Clock className="h-2.5 w-2.5" />
                  Marge restante
                </p>
                <p className={cn(
                  "text-xl font-black font-mono leading-none",
                  jours_restants !== undefined && jours_restants <= 3 ? "text-red-500" :
                  jours_restants !== undefined && jours_restants <= 7 ? "text-yellow-500" : "text-foreground"
                )}>
                  {jours_restants !== undefined ? (
                    <>
                      {jours_restants > 0 ? jours_restants : 0}
                      <span className="text-xs font-semibold ml-0.5">
                        {jours_restants <= 1 ? "jour de marge" : "jours de marge"}
                      </span>
                    </>
                  ) : (
                    "—"
                  )}
                </p>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Jours restants avant déficit
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Frise temporelle d'avancement du chantier - Style segmenté gaming */}
        {date_debut_prevue && date_fin_estimee && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                "p-2.5 rounded-lg bg-muted/30 cursor-help space-y-2",
                // Grisé si suspendu ou annulé
                (etat_chantier === 'suspendu' || etat_chantier === 'annule') && "opacity-50"
              )}>
                {(() => {
                  const dateDebut = new Date(date_debut_prevue);
                  const dateFin = new Date(date_fin_estimee);
                  const today = new Date();

                  // États du chantier
                  const isCompleted = etat_chantier === 'termine';
                  const isSuspenduState = etat_chantier === 'suspendu';
                  const isArchived = etat_chantier === 'archive';
                  const isAnnule = etat_chantier === 'annule';
                  const isDeficitaire = rentabilite < 0 && etat_chantier === 'en_cours';

                  // Vérifier si le chantier a été repris récemment (moins de 48h)
                  const isRecentlyResumed = dateReprise
                    ? (today.getTime() - new Date(dateReprise).getTime()) < (48 * 60 * 60 * 1000)
                    : false;

                  // Durée totale en jours calendaires
                  const calendarDays = Math.max(1, (dateFin.getTime() - dateDebut.getTime()) / (1000 * 60 * 60 * 24));
                  const totalJoursOuvres = (duree_estimee_jours || calendarDays) + (joursRetardDeclareGlobal || 0);

                  // Jours calendaires écoulés depuis le début (pour badge "Début J-X")
                  const elapsedCalendarDays = (today.getTime() - dateDebut.getTime()) / (1000 * 60 * 60 * 24);

                  // Progression temporelle : source unique = serveur (compute_chantier_metrics)
                  const progressPct = progress_pct ?? 0;

                  // Calculer fractionElapsed pour compatibilité avec le reste du code
                  const fractionElapsed = progressPct / 100;

                  // Jours ouvrés restants
                  const joursOuvresRestants = totalJoursOuvres - (fractionElapsed * totalJoursOuvres);

                  // Statut temporel
                  const isNotStarted = today < dateDebut;
                  const isDateFinDepassee = today > dateFin;

                  // Nombre de jours de retard déclarés (utilise la variable globale calculée plus haut)
                  const joursRetardDeclare = joursRetardDeclareGlobal;

                  // Date de fin INITIALE (avant retard) = date début + (durée - 1) jours ouvrés
                  // Date de fin INITIALE (avant retard)
                  // Priorité: 1) date_fin_initiale depuis la DB, 2) calcul via duree_estimee, 3) dateFin
                  const dateFinInitiale = hasDelayRegistered
                    ? (date_fin_initiale
                        ? new Date(date_fin_initiale)
                        : duree_estimee_jours
                          ? (addBusinessDays(date_debut_prevue!, Math.ceil(duree_estimee_jours) - 1) || dateFin)
                          : dateFin)
                    : dateFin;

                  // Calcul pour la suspension
                  const joursSuspension = suspensionDate
                    ? Math.max(0, Math.floor((today.getTime() - new Date(suspensionDate).getTime()) / (1000 * 60 * 60 * 24)))
                    : 0;

                  // Jours avant reprise estimée
                  const joursAvantReprise = suspensionRepriseEstimee
                    ? Math.max(0, Math.ceil((new Date(suspensionRepriseEstimee).getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
                    : null;

                  // Ratio pour conversion jours calendaires → ouvrés
                  const overdueRatio = calendarDays > 0 ? totalJoursOuvres / calendarDays : 1;

                  // Nombre de segments pour la barre (20 segments = 5% chacun)
                  const totalSegments = 20;

                  // Calcul des segments selon l'état
                  let segmentsBleus = 0;
                  let segmentsOranges = 0;
                  let segmentsVerts = 0;

                  if (isCompleted || isArchived) {
                    // Terminé ou Archivé → tout vert
                    segmentsVerts = totalSegments;
                  } else if (hasDelayRegistered && joursRetardDeclare > 0 && dateFinRetard) {
                    // Retard déclaré → progression réelle sur durée totale

                    // Ratio bleu/orange : tout en jours ouvrés
                    const joursOuvresInitiaux = duree_estimee_jours || 0;
                    const joursOuvresRetard = joursRetardDeclare || 0;
                    const joursOuvresTotal = joursOuvresInitiaux + joursOuvresRetard;

                    // Calcul du ratio seulement si on a des données valides
                    let ratioBleu = 1; // Par défaut tout bleu
                    let segmentsPartieInitiale = totalSegments;

                    if (joursOuvresTotal > 0 && joursOuvresRetard > 0) {
                      // Il y a un retard déclaré → calculer le ratio
                      ratioBleu = joursOuvresInitiaux / joursOuvresTotal;
                      segmentsPartieInitiale = Math.round(ratioBleu * totalSegments);
                    }

                    // Remplir les segments selon la progression
                    const totalSegmentsRemplis = Math.round((progressPct / 100) * totalSegments);
                    segmentsBleus = Math.min(totalSegmentsRemplis, segmentsPartieInitiale);
                    segmentsOranges = Math.max(0, totalSegmentsRemplis - segmentsBleus);
                  } else if (isSuspenduState || isAnnule) {
                    // Suspendu ou annulé → figé à la progression actuelle
                    segmentsBleus = Math.round((progressPct / 100) * totalSegments);
                  } else {
                    // Normal → progression standard
                    segmentsBleus = Math.round((progressPct / 100) * totalSegments);
                  }

                  // Formatage des jours avec demi-journées
                  const formatJours = (jours: number): string => {
                    if (jours <= 0) return '0';
                    const rounded = Math.round(jours * 2) / 2;
                    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
                  };

                  // Badge texte selon l'état
                  const getBadgeText = () => {
                    // États prioritaires
                    if (isCompleted) return '✓ Terminé';
                    if (isAnnule) return '❌ Annulé';
                    if (isSuspenduState) {
                      if (joursAvantReprise !== null) {
                        return `⏸️ Reprise J-${joursAvantReprise}`;
                      }
                      return `⏸️ Suspendu +${joursSuspension}j`;
                    }
                    if (isRecentlyResumed) return '▶️ Repris';
                    if (hasDelayRegistered) return `+${formatJours(joursRetardDeclare)}j retard`;

                    // Date dépassée sans retard déclaré → inciter à clôturer
                    if (isDateFinDepassee && etat_chantier === 'en_cours') return 'Clôturer ?';

                    // Déficitaire
                    if (isDeficitaire) return `⚠️ J-${formatJours(joursOuvresRestants)}`;

                    // Normal
                    if (isNotStarted) {
                      const joursAvantDebut = Math.abs(elapsedCalendarDays) * overdueRatio;
                      return `Début J-${formatJours(joursAvantDebut)}`;
                    }
                    return `J-${formatJours(joursOuvresRestants)}`;
                  };

                  // Styles de base pour les badges avec flèches
                  const badgeStyles = {
                    default: 'px-2.5 py-0.5 rounded-md font-bold cursor-pointer transition-colors',
                    pulse: 'px-2.5 py-0.5 rounded-md font-bold animate-pulse cursor-pointer transition-colors',
                  };

                  // Couleur du badge selon état et mode
                  const getBadgeColor = () => {
                    // Archivé : Archivé ↔ Désarchiver ↔ Supprimer
                    if (etat_chantier === 'archive') {
                      if (archiveBadgeMode === 'desarchiver') return `bg-blue-500 text-white ${badgeStyles.pulse} hover:bg-blue-400`;
                      return `bg-red-500 text-white ${badgeStyles.pulse} hover:bg-red-400`; // supprimer
                    }
                    // Terminé : Terminé ↔ Archiver
                    if (isCompleted) {
                      return termineBadgeMode === 'reouvrir'
                        ? `bg-blue-500 text-white ${badgeStyles.pulse} hover:bg-blue-400`
                        : `bg-slate-500 text-white ${badgeStyles.pulse} hover:bg-slate-400`;
                    }
                    // Annulé : Annulé ↔ Archiver ↔ Supprimer
                    if (isAnnule) {
                      if (annuleBadgeMode === 'archiver') return `bg-slate-500 text-white ${badgeStyles.pulse} hover:bg-slate-400`;
                      return `bg-red-600 text-white ${badgeStyles.pulse} hover:bg-red-500`; // supprimer
                    }
                    // Suspendu : Suspendu ↔ Reprise ↔ Annuler ↔ Terminer
                    if (isSuspenduState) {
                      if (suspenduBadgeMode === 'suspendu') return `bg-orange-500 text-black ${badgeStyles.default} hover:bg-orange-400`;
                      if (suspenduBadgeMode === 'reprise') return `bg-green-500 text-black ${badgeStyles.pulse} hover:bg-green-400`;
                      if (suspenduBadgeMode === 'annuler') return `bg-red-500 text-white ${badgeStyles.pulse} hover:bg-red-400`;
                      return `bg-emerald-500 text-black ${badgeStyles.pulse} hover:bg-emerald-400`; // terminer
                    }
                    // Repris récemment : Repris ↔ Suspendre
                    if (isRecentlyResumed) {
                      return reprisBadgeMode === 'repris'
                        ? `bg-green-500 text-black ${badgeStyles.default} hover:bg-green-400`
                        : `bg-orange-500 text-black ${badgeStyles.pulse} hover:bg-orange-400`;
                    }
                    // Retard enregistré : Retard ↔ Clôturer
                    if (hasDelayRegistered) {
                      return retardBadgeMode === 'retard'
                        ? `bg-orange-500 text-black ${badgeStyles.default} hover:bg-orange-400`
                        : `bg-amber-500 text-black ${badgeStyles.pulse} hover:bg-amber-400`;
                    }
                    // Date dépassée : Clôturer ↔ Retard
                    if (isDateFinDepassee && etat_chantier === 'en_cours') {
                      return badgeMode === 'cloturer'
                        ? `bg-amber-500 text-black ${badgeStyles.pulse} hover:bg-amber-400`
                        : `bg-orange-500 text-black ${badgeStyles.pulse} hover:bg-orange-400`;
                    }
                    // Brouillon : Brouillon ↔ Démarrer ↔ Supprimer
                    if (etat_chantier === 'brouillon') {
                      if (brouillonBadgeMode === 'demarrer') return `bg-green-500 text-black ${badgeStyles.pulse} hover:bg-green-400`;
                      return `bg-red-500 text-white ${badgeStyles.pulse} hover:bg-red-400`; // supprimer
                    }
                    // Déficitaire et autres
                    if (isDeficitaire) return 'text-orange-500';
                    return 'text-amber-500';
                  };

                  // Pourcentage affiché (basé sur heures ouvrées via progressPct)
                  const getDisplayPercentage = () => {
                    if (isCompleted || isArchived) return '100%';
                    if (isAnnule) return '❌';
                    if (isSuspenduState) return '⏸️';

                    // progressPct est déjà calculé via calculateRealProgressHours
                    // qui prend en compte les heures ouvrées, le retard, etc.
                    return `${Math.round(progressPct)}%`;
                  };

                  // Couleur des segments (avec bordure blanche pour les segments actifs)
                  const getSegmentColor = (index: number) => {
                    const activeBorder = "border border-white/50";
                    const inactiveStyle = "bg-slate-700/50";

                    if (isCompleted || isArchived) {
                      return index < segmentsVerts ? `bg-green-500 ${activeBorder}` : inactiveStyle;
                    }
                    if (isAnnule || isSuspenduState) {
                      return index < segmentsBleus ? `bg-slate-500 ${activeBorder}` : inactiveStyle;
                    }
                    if (hasDelayRegistered && joursRetardDeclare > 0) {
                      if (index < segmentsBleus) return `bg-primary ${activeBorder}`;
                      if (index < segmentsBleus + segmentsOranges) return `bg-orange-500 ${activeBorder}`;
                      return inactiveStyle;
                    }
                    // Normal ou date dépassée sans retard → utiliser segmentsBleus
                    return index < segmentsBleus ? `bg-primary ${activeBorder}` : inactiveStyle;
                  };

                  // Dates affichées
                  const getLeftDate = () => {
                    if (hasDelayRegistered && joursRetardDeclare > 0) {
                      // Afficher la période INITIALE (avant retard)
                      // Si même mois: "26 → 29 janv." sinon "26 janv. → 29 févr."
                      const debutMonth = dateDebut.getMonth();
                      const finInitialeMonth = dateFinInitiale.getMonth();

                      if (debutMonth === finInitialeMonth) {
                        // Même mois: afficher le mois une seule fois
                        const debutDay = dateDebut.getDate();
                        const finDay = dateFinInitiale.getDate();
                        const monthStr = dateDebut.toLocaleDateString('fr-FR', { month: 'short' });
                        return `${debutDay} → ${finDay} ${monthStr}`;
                      } else {
                        // Mois différents: afficher les deux
                        const debutStr = dateDebut.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                        const finInitialeStr = dateFinInitiale.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                        return `${debutStr} → ${finInitialeStr}`;
                      }
                    }
                    return dateDebut.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                  };

                  const getRightDate = () => {
                    if (isAnnule || isSuspenduState) return '--';
                    if (hasDelayRegistered && dateFinRetard) {
                      return new Date(dateFinRetard).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                    }
                    return dateFin.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                  };

                  // Tooltip personnalisé
                  const getTooltipText = () => {
                    if (isRecentlyResumed && dateReprise) {
                      const dateSuspensionStr = suspensionDate
                        ? new Date(suspensionDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                        : '?';
                      const dateRepriseStr = new Date(dateReprise).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                      return `Suspendu du ${dateSuspensionStr} au ${dateRepriseStr}`;
                    }
                    if (isDeficitaire) {
                      return `Marge négative : ${rentabilite.toFixed(1)}%`;
                    }
                    return `Avancement temporel (${duree_estimee_jours ? `${duree_estimee_jours} jours travaillés` : 'jours calendaires'})`;
                  };

                  // Handler pour "Clôturer" → Change le statut en "terminé"
                  const handleBadgeClick = (e?: React.MouseEvent) => {
                    e?.stopPropagation();
                    if (onChangeState) {
                      onChangeState(id, 'termine');
                    }
                  };

                  return (
                    <>
                      {/* Barre segmentée style pill - 20 segments arrondis */}
                      <div className="flex items-center gap-1">
                        <div className="flex-1 flex gap-1 overflow-hidden">
                          {Array.from({ length: totalSegments }).map((_, i) => (
                            <div
                              key={i}
                              className={cn(
                                "flex-1 h-2 rounded-full transition-all duration-300",
                                getSegmentColor(i)
                              )}
                            />
                          ))}
                        </div>
                        {/* Pourcentage ou icône */}
                        <span className={cn(
                          "text-xs font-black ml-2 min-w-[36px] text-right",
                          (isAnnule || isSuspenduState) ? "text-slate-400" : "text-white"
                        )}>
                          {getDisplayPercentage()}
                        </span>
                      </div>

                      {/* Ligne du bas: dates + badge (grille 3 colonnes pour centrer le badge) */}
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1">
                        {/* Colonne gauche: date de début */}
                        <span className={cn(
                          "text-xs font-medium text-left",
                          (isAnnule || isSuspenduState) ? "text-slate-400" : "text-white"
                        )}>
                          {getLeftDate()}
                        </span>
{/* === Colonne centrale: BADGES AVEC FLÈCHES SELON L'ÉTAT === */}
                        {(() => {
                          // Helper pour les flèches
                          const ArrowButton = ({ onClick, direction }: { onClick: () => void; direction: 'left' | 'right' }) => (
                            <button
                              onClick={(e) => { e.stopPropagation(); onClick(); }}
                              className="px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                            >
                              <span className="text-[8px] text-white">{direction === 'left' ? '◀' : '▶'}</span>
                            </button>
                          );

                          // Helper pour cycler entre les options
                          const cycleOptions = <T extends string>(current: T, options: T[], direction: 'next' | 'prev'): T => {
                            const idx = options.indexOf(current);
                            if (direction === 'next') return options[(idx + 1) % options.length];
                            return options[(idx - 1 + options.length) % options.length];
                          };

                          // === CAS 1: ARCHIVÉ → Archivé / Désarchiver / Supprimer ===
                          if (etat_chantier === 'archive') {
                            const options: ('desarchiver' | 'supprimer')[] = ['desarchiver', 'supprimer'];
                            const labels = { desarchiver: 'Désarchiver', supprimer: 'Supprimer' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setArchiveBadgeMode(cycleOptions(archiveBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (archiveBadgeMode === 'desarchiver' && onUnarchive) onUnarchive(id);
                                    if (archiveBadgeMode === 'supprimer') setShowDeleteConfirm(true);
                                  }}
                                >
                                  {labels[archiveBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setArchiveBadgeMode(cycleOptions(archiveBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 2: TERMINÉ → Réouvrir / Archiver ===
                          if (isCompleted) {
                            const options: ('reouvrir' | 'archiver')[] = ['reouvrir', 'archiver'];
                            const labels = { reouvrir: 'Réouvrir', archiver: 'Archiver' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setTermineBadgeMode(cycleOptions(termineBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (termineBadgeMode === 'reouvrir') setShowReopenConfirm(true);
                                    if (termineBadgeMode === 'archiver' && onArchive) onArchive(id);
                                  }}
                                >
                                  {labels[termineBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setTermineBadgeMode(cycleOptions(termineBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 3: ANNULÉ → Annulé / Archiver / Supprimer ===
                          if (isAnnule) {
                            const options: ('archiver' | 'supprimer')[] = ['archiver', 'supprimer'];
                            const labels = { archiver: 'Archiver', supprimer: 'Supprimer' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setAnnuleBadgeMode(cycleOptions(annuleBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (annuleBadgeMode === 'archiver' && onArchive) onArchive(id);
                                    if (annuleBadgeMode === 'supprimer') setShowDeleteConfirm(true);
                                  }}
                                >
                                  {labels[annuleBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setAnnuleBadgeMode(cycleOptions(annuleBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 4-5: SUSPENDU → Suspendu / Reprise / Annuler / Terminer ===
                          if (isSuspenduState) {
                            const options: ('suspendu' | 'reprise' | 'annuler' | 'terminer')[] = ['suspendu', 'reprise', 'annuler', 'terminer'];
                            const labels = {
                              suspendu: joursAvantReprise !== null ? `⏸️ Reprise J-${joursAvantReprise}` : `⏸️ Suspendu +${joursSuspension}j`,
                              reprise: 'Reprise',
                              annuler: 'Annuler',
                              terminer: 'Terminer'
                            };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setSuspenduBadgeMode(cycleOptions(suspenduBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (suspenduBadgeMode === 'reprise' && onChangeState) onChangeState(id, 'reprise');
                                    if (suspenduBadgeMode === 'annuler' && onChangeState) onChangeState(id, 'annule');
                                    if (suspenduBadgeMode === 'terminer' && onChangeState) onChangeState(id, 'termine');
                                  }}
                                >
                                  {labels[suspenduBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setSuspenduBadgeMode(cycleOptions(suspenduBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 6: REPRIS RÉCEMMENT → Repris / Suspendre ===
                          if (isRecentlyResumed) {
                            const options: ('repris' | 'suspendre')[] = ['repris', 'suspendre'];
                            const labels = { repris: '▶️ Repris', suspendre: 'Suspendre' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setReprisBadgeMode(cycleOptions(reprisBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (reprisBadgeMode === 'suspendre' && onChangeState) onChangeState(id, 'suspendu');
                                  }}
                                >
                                  {labels[reprisBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setReprisBadgeMode(cycleOptions(reprisBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 7b: FIN RETARD DÉPASSÉE → Clôturer / Prolonger ===
                          // Quand la date de fin du retard est dépassée, proposer de clôturer ou prolonger
                          if (isFinRetardDepassee) {
                            const options: ('cloturer' | 'prolonger')[] = ['cloturer', 'prolonger'];
                            const labels = { cloturer: 'Clôturer', prolonger: 'Prolonger' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setFinRetardBadgeMode(cycleOptions(finRetardBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn(
                                    "text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse",
                                    finRetardBadgeMode === 'prolonger'
                                      ? "bg-white text-black cursor-pointer"
                                      : "bg-orange-500 text-white cursor-pointer"
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (finRetardBadgeMode === 'cloturer' && onChangeState) onChangeState(id, 'termine');
                                    if (finRetardBadgeMode === 'prolonger') {
                                      if (onProlongerRetard) {
                                        onProlongerRetard(id);
                                      }
                                    }
                                  }}
                                >
                                  {labels[finRetardBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setFinRetardBadgeMode(cycleOptions(finRetardBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 7: RETARD EN COURS → Modifier / Clôturer ===
                          // Quand un retard est enregistré mais la période n'est pas encore terminée
                          if (hasDelayRegistered) {
                            const options: ('modifier' | 'cloturer')[] = ['modifier', 'cloturer'];
                            const labels = { modifier: 'Modifier', cloturer: 'Clôturer' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setRetardBadgeMode(cycleOptions(retardBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn(
                                    "text-[10px] font-bold px-2 py-0.5 rounded-full",
                                    retardBadgeMode === 'modifier'
                                      ? "bg-white text-black cursor-pointer"
                                      : getBadgeColor()
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (retardBadgeMode === 'modifier') navigate('/financial?tab=retard', { state: { chantierId: id, openEditMode: true } });
                                    if (retardBadgeMode === 'cloturer' && onChangeState) onChangeState(id, 'termine');
                                  }}
                                >
                                  {labels[retardBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setRetardBadgeMode(cycleOptions(retardBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 8: DATE DÉPASSÉE (en cours, sans retard) → Clôturer / Retard ===
                          if (isDateFinDepassee && etat_chantier === 'en_cours') {
                            const options: ('cloturer' | 'retard')[] = ['cloturer', 'retard'];
                            const labels = { cloturer: 'Clôturer', retard: 'Retard' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setBadgeMode(cycleOptions(badgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (badgeMode === 'cloturer' && onChangeState) onChangeState(id, 'termine');
                                    if (badgeMode === 'retard') navigate(`/financial/${id}?tab=penalty-simulator`);
                                  }}
                                >
                                  {labels[badgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setBadgeMode(cycleOptions(badgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS 9: BROUILLON → Brouillon / Démarrer / Supprimer ===
                          if (etat_chantier === 'brouillon') {
                            const options: ('demarrer' | 'supprimer')[] = ['demarrer', 'supprimer'];
                            const labels = { demarrer: 'Démarrer', supprimer: 'Supprimer' };
                            return (
                              <div className="flex items-center gap-1">
                                <ArrowButton direction="left" onClick={() => setBrouillonBadgeMode(cycleOptions(brouillonBadgeMode, options, 'prev'))} />
                                <span
                                  className={cn("text-[10px] font-bold", getBadgeColor())}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (brouillonBadgeMode === 'demarrer') {
                                      // Utiliser onDemarrerBrouillon pour vérifier dates et conflits
                                      if (onDemarrerBrouillon) {
                                        onDemarrerBrouillon(id);
                                      } else if (onChangeState) {
                                        // Fallback si pas de handler spécifique
                                        onChangeState(id, 'en_cours');
                                      }
                                    }
                                    if (brouillonBadgeMode === 'supprimer') setShowDeleteConfirm(true);
                                  }}
                                >
                                  {labels[brouillonBadgeMode]}
                                </span>
                                <ArrowButton direction="right" onClick={() => setBrouillonBadgeMode(cycleOptions(brouillonBadgeMode, options, 'next'))} />
                              </div>
                            );
                          }

                          // === CAS PAR DÉFAUT: Badge simple sans flèches ===
                          return (
                            <span
                              className={cn("text-[10px] font-bold", getBadgeColor())}
                              onClick={handleBadgeClick}
                            >
                              {getBadgeText()}
                            </span>
                          );
                        })()}
                        {/* Colonne droite: date de fin */}
                        <span className={cn(
                          "text-xs font-medium text-right",
                          (isAnnule || isSuspenduState) ? "text-slate-400" :
                          (hasDelayRegistered && dateFinRetard) ? "text-orange-500" : "text-white"
                        )}>
                          {getRightDate()}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {(() => {
                const today = new Date();
                const dateFin = new Date(date_fin_estimee);
                const isDateFinDepassee = today > dateFin;
                const isDeficitaire = rentabilite < 0 && etat_chantier === 'en_cours';
                const isRecentlyResumed = dateReprise
                  ? (today.getTime() - new Date(dateReprise).getTime()) < (48 * 60 * 60 * 1000)
                  : false;

                if (isRecentlyResumed && suspensionDate && dateReprise) {
                  const dateSuspensionStr = new Date(suspensionDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                  const dateRepriseStr = new Date(dateReprise).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                  return `Suspendu du ${dateSuspensionStr} au ${dateRepriseStr}`;
                }
                if (isDeficitaire) {
                  return `Marge négative : ${rentabilite.toFixed(1)}%`;
                }
                if (isDateFinDepassee && etat_chantier === 'en_cours' && !hasDelayRegistered) {
                  return 'Cliquez sur "Clôturer ?" pour terminer le chantier';
                }
                // Fin de retard dépassée
                if (hasDelayRegistered && dateFinRetard && new Date(dateFinRetard) < today && etat_chantier === 'en_cours') {
                  return 'Période de retard terminée. Clôturer ou prolonger ?';
                }
                return `Avancement temporel (${duree_estimee_jours ? `${duree_estimee_jours} jours travaillés` : 'jours calendaires'})`;
              })()}
            </TooltipContent>
          </Tooltip>
        )}
      </CardContent>

      <CardFooter className="p-4 pt-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "w-full font-medium text-xs h-8 group/btn",
                isHighlighted && "animate-highlight-pulse-light"
              )}
              onClick={() => {
                // Vérifier si l'utilisateur a déjà vu le tips devis
                const tipsSeen = localStorage.getItem('b8ild_tips_devis_seen');
                navigate(`/projects/${id}`, {
                  state: tipsSeen ? undefined : { showDevisTips: true }
                });
              }}
            >
              Voir détails
              <span className="ml-1.5 group-hover/btn:translate-x-0.5 transition-transform">→</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Accéder à la fiche complète
          </TooltipContent>
        </Tooltip>
      </CardFooter>

      {/* Modal de confirmation de réouverture */}
      <AlertDialog open={showReopenConfirm} onOpenChange={setShowReopenConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réouvrir ce chantier ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le chantier "{nom_chantier}" repassera en cours. Les métriques seront recalculées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (onChangeState) onChangeState(id, 'en_cours');
                setShowReopenConfirm(false);
              }}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Réouvrir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmation de suppression */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce chantier ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le chantier "{nom_chantier}" et toutes ses données associées seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (onDelete) onDelete(id);
                setShowDeleteConfirm(false);
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ProjectCard;
